import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API || "http://localhost:3001";

/* ══════════════════════════════════════════════
   UTILS
   ══════════════════════════════════════════════ */

const getToken = () => sessionStorage.getItem("controla_admin_token");
const getSession = () => {
  try { return JSON.parse(sessionStorage.getItem("controla_admin_session")); } catch { return null; }
};
const clearSession = () => { sessionStorage.removeItem("controla_admin_token"); sessionStorage.removeItem("controla_admin_session"); };

async function apiFetch(path, opts = {}) {
  const token = getToken();
  const res = await fetch(API + path, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    method: opts.method || "GET",
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) { clearSession(); window.location.reload(); throw new Error("Sessão expirada"); }
    throw new Error(data.error || "Erro na requisição");
  }
  return data;
}

const fmt = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v);
  const d = s.includes("T") ? s.split("T")[0] : s;
  if (d.length === 10 && d.includes("-")) { const [y, m, day] = d.split("-"); return day + "/" + m + "/" + y; }
  return s;
};

const statusColors = {
  ativo: "#2ecc71",
  trial: "#3498db",
  suspenso: "#f39c12",
  cancelado: "#e74c3c",
};

const statusLabels = {
  ativo: "Ativo",
  trial: "Trial",
  suspenso: "Suspenso",
  cancelado: "Cancelado",
};

const planoLabels = {
  basico: "Básico",
  profissional: "Profissional",
  enterprise: "Enterprise",
};

/* ══════════════════════════════════════════════
   GLOBAL STYLES
   ══════════════════════════════════════════════ */

const GlobalStyles = () => (
  <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', -apple-system, sans-serif; background: #0f0f23; color: #e0e0e0; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #1a1a2e; }
    ::-webkit-scrollbar-thumb { background: #4a4a6a; border-radius: 3px; }
    input, select, textarea {
      background: #16213e; border: 1px solid #2a2a4a; color: #e0e0e0;
      padding: 10px 14px; border-radius: 8px; font-size: 14px; width: 100%;
      transition: border-color .2s;
    }
    input:focus, select:focus, textarea:focus { outline: none; border-color: #e74c3c; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: scale(1); } }
  `}</style>
);

/* ══════════════════════════════════════════════
   LOGIN PAGE
   ══════════════════════════════════════════════ */

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro(""); setLoading(true);
    try {
      const data = await apiFetch("/api/admin/login", { method: "POST", body: { email, senha } });
      sessionStorage.setItem("controla_admin_token", data.token);
      sessionStorage.setItem("controla_admin_session", JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) {
      setErro(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)", position: "relative", overflow: "hidden" }}>
      {/* Background icons */}
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{ position: "absolute", fontSize: 60 + i * 10, opacity: 0.04, top: `${10 + i * 12}%`, left: `${5 + i * 12}%`, transform: `rotate(${i * 25}deg)` }}>⚙️</div>
      ))}
      <form onSubmit={handleLogin} style={{ background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)", borderRadius: 20, padding: "50px 40px", width: 420, boxShadow: "0 20px 60px rgba(0,0,0,.5)", position: "relative", zIndex: 1, animation: "slideIn .4s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, #e74c3c, #c0392b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 16px", boxShadow: "0 4px 20px rgba(231,76,60,.4)" }}>⚙️</div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#e74c3c", textTransform: "uppercase" }}>Painel Administrativo</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", margin: "6px 0 4px" }}>Controla CAÇAMBA</h1>
          <p style={{ color: "#8888aa", fontSize: 13 }}>Gerenciamento de Tenants</p>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#8888aa", marginBottom: 6, textTransform: "uppercase" }}>Email</label>
          <input type="email" placeholder="Digite seu email" value={email} onChange={e => setEmail(e.target.value)} required style={{ borderColor: "#2a2a4a" }} />
        </div>
        <div style={{ marginBottom: 24, position: "relative" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#8888aa", marginBottom: 6, textTransform: "uppercase" }}>Senha</label>
          <input type={showPw ? "text" : "password"} placeholder="Digite sua senha" value={senha} onChange={e => setSenha(e.target.value)} required />
          <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 12, top: 34, background: "none", border: "none", color: "#8888aa", cursor: "pointer", fontSize: 16 }}>{showPw ? "🙈" : "👁️"}</button>
        </div>
        {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{erro}</div>}
        <button type="submit" disabled={loading} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #e74c3c, #c0392b)", color: "#fff", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", letterSpacing: 1, opacity: loading ? .7 : 1 }}>
          {loading ? "Entrando..." : "Entrar no Painel"}
        </button>
        <p style={{ textAlign: "center", color: "#4a4a6a", fontSize: 12, marginTop: 24 }}>Controla CAÇAMBA Admin v1.0.0</p>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════
   TOAST
   ══════════════════════════════════════════════ */

function ToastContainer({ toasts }) {
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999, display: "flex", flexDirection: "column", gap: 10 }}>
      {toasts.map(t => (
        <div key={t.id} style={{ background: "#1a1a2e", border: `1px solid ${t.color || "#e74c3c"}`, borderRadius: 12, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 30px rgba(0,0,0,.4)", animation: "fadeIn .3s ease", minWidth: 280 }}>
          <span style={{ fontSize: 22 }}>{t.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>{t.title}</div>
            {t.sub && <div style={{ fontSize: 12, color: "#8888aa", marginTop: 2 }}>{t.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SIDEBAR
   ══════════════════════════════════════════════ */

function Sidebar({ page, setPage, admin, onLogout }) {
  const items = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "tenants", icon: "🏢", label: "Tenants" },
  ];

  return (
    <div style={{ width: 240, background: "#1a1a2e", borderRight: "1px solid #2a2a4a", display: "flex", flexDirection: "column", height: "100vh", position: "fixed", left: 0, top: 0, zIndex: 100 }}>
      {/* Header */}
      <div style={{ padding: "24px 20px 20px", borderBottom: "1px solid #2a2a4a" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #e74c3c, #c0392b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚙️</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#fff" }}>Admin Panel</div>
            <div style={{ fontSize: 11, color: "#e74c3c" }}>Controla Caçamba</div>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div style={{ flex: 1, padding: "12px 10px" }}>
        {items.map(item => (
          <button key={item.id} onClick={() => setPage(item.id)} style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 14px",
            background: page === item.id ? "rgba(231,76,60,.15)" : "transparent",
            border: page === item.id ? "1px solid rgba(231,76,60,.3)" : "1px solid transparent",
            borderRadius: 10, cursor: "pointer", color: page === item.id ? "#e74c3c" : "#8888aa",
            fontSize: 14, fontWeight: page === item.id ? 700 : 500, marginBottom: 4, transition: "all .2s",
          }}>
            <span style={{ fontSize: 18 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>

      {/* User */}
      <div style={{ padding: "16px", borderTop: "1px solid #2a2a4a" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#e74c3c", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, color: "#fff" }}>
            {(admin?.nome?.[0] || "A").toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#fff" }}>{admin?.nome || "Admin"}</div>
            <div style={{ fontSize: 11, color: "#8888aa" }}>Super Admin</div>
          </div>
        </div>
        <button onClick={onLogout} style={{ width: "100%", padding: "8px", background: "rgba(231,76,60,.1)", border: "1px solid rgba(231,76,60,.2)", borderRadius: 8, color: "#e74c3c", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Sair
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   STATUS BADGE
   ══════════════════════════════════════════════ */

function StatusBadge({ status }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${statusColors[status] || "#666"}22`, border: `1px solid ${statusColors[status] || "#666"}44`, color: statusColors[status] || "#666", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColors[status] || "#666" }} />
      {statusLabels[status] || status}
    </span>
  );
}

/* ══════════════════════════════════════════════
   DASHBOARD
   ══════════════════════════════════════════════ */

function Dashboard({ stats, loading }) {
  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#8888aa" }}>Carregando...</div>;

  const cards = [
    { icon: "🏢", label: "Tenants Ativos", value: stats?.tenants?.ativos || 0, color: "#2ecc71" },
    { icon: "🧪", label: "Em Trial", value: stats?.tenants?.trial || 0, color: "#3498db" },
    { icon: "⏸️", label: "Suspensos", value: stats?.tenants?.suspensos || 0, color: "#f39c12" },
    { icon: "❌", label: "Cancelados", value: stats?.tenants?.cancelados || 0, color: "#e74c3c" },
    { icon: "📦", label: "Total Locações", value: stats?.locacoes || 0, color: "#9b59b6" },
    { icon: "👥", label: "Total Usuários", value: stats?.usuarios || 0, color: "#1abc9c" },
    { icon: "💰", label: "Faturamento Global", value: fmt(stats?.faturamento || 0), color: "#f1c40f" },
    { icon: "🏢", label: "Total Tenants", value: stats?.tenants?.total || 0, color: "#e74c3c" },
  ];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 8 }}>Dashboard</h1>
      <p style={{ color: "#8888aa", fontSize: 14, marginBottom: 30 }}>Visão geral do sistema</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 30 }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 14, padding: "20px", animation: `fadeIn .3s ease ${i * .05}s both` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${c.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{c.icon}</div>
              <span style={{ fontSize: 12, color: "#8888aa", fontWeight: 600 }}>{c.label}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Trial expirando */}
      {stats?.trialExpirando?.length > 0 && (
        <div style={{ background: "#1a1a2e", border: "1px solid #f39c1244", borderRadius: 14, padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#f39c12", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            ⚠️ Trials Expirando em 7 Dias
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stats.trialExpirando.map(t => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#16213e", borderRadius: 8 }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#fff" }}>{t.nome}</span>
                  <span style={{ color: "#8888aa", fontSize: 13, marginLeft: 10 }}>{t.email}</span>
                </div>
                <span style={{ color: "#f39c12", fontSize: 13, fontWeight: 600 }}>Expira: {fmtDate(t.trial_expira)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MODAL BASE
   ══════════════════════════════════════════════ */

function Modal({ title, onClose, children, width }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 16, width: width || 520, maxHeight: "90vh", overflow: "auto", animation: "slideIn .3s ease" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #2a2a4a" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888aa", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   NOVO TENANT MODAL
   ══════════════════════════════════════════════ */

function NovoTenantModal({ onClose, onSave }) {
  const [form, setForm] = useState({ nome: "", email: "", plano: "basico", trialDias: 14, nomeUsuario: "", loginUsuario: "", senhaUsuario: "", emailUsuario: "" });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.nome || !form.email) return setErro("Nome e email são obrigatórios");
    if (!form.nomeUsuario || !form.loginUsuario || !form.senhaUsuario) return setErro("Dados do primeiro usuário são obrigatórios");
    setLoading(true); setErro("");
    try {
      await onSave(form);
      onClose();
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const fieldStyle = { marginBottom: 14 };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#8888aa", marginBottom: 6, textTransform: "uppercase" };

  return (
    <Modal title="Novo Tenant" onClose={onClose} width={560}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Nome da Empresa</label>
          <input value={form.nome} onChange={e => upd("nome", e.target.value)} placeholder="Ex: Caçambas São Paulo LTDA" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input type="email" value={form.email} onChange={e => upd("email", e.target.value)} placeholder="contato@empresa.com" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Plano</label>
          <select value={form.plano} onChange={e => upd("plano", e.target.value)}>
            <option value="basico">Básico</option>
            <option value="profissional">Profissional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Dias de Trial</label>
          <input type="number" value={form.trialDias} onChange={e => upd("trialDias", e.target.value)} min={1} max={90} />
        </div>

        <div style={{ gridColumn: "1 / -1", margin: "10px 0 14px", padding: "12px 0 0", borderTop: "1px solid #2a2a4a" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e74c3c" }}>Primeiro Usuário (Admin do Tenant)</span>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Nome</label>
          <input value={form.nomeUsuario} onChange={e => upd("nomeUsuario", e.target.value)} placeholder="Nome completo" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Login</label>
          <input value={form.loginUsuario} onChange={e => upd("loginUsuario", e.target.value)} placeholder="login de acesso" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Senha</label>
          <input type="text" value={form.senhaUsuario} onChange={e => upd("senhaUsuario", e.target.value)} placeholder="Senha inicial" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email do Usuário</label>
          <input type="email" value={form.emailUsuario} onChange={e => upd("emailUsuario", e.target.value)} placeholder="email@usuario.com" />
        </div>
      </div>

      {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}

      <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "10px 24px", background: "#16213e", border: "1px solid #2a2a4a", borderRadius: 8, color: "#8888aa", fontSize: 14, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #e74c3c, #c0392b)", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: loading ? .7 : 1 }}>
          {loading ? "Criando..." : "Criar Tenant"}
        </button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   EDITAR TENANT MODAL
   ══════════════════════════════════════════════ */

function EditarTenantModal({ tenant, onClose, onSave }) {
  const [form, setForm] = useState({ nome: tenant.nome, email: tenant.email, plano: tenant.plano, status: tenant.status, trial_expira: tenant.trial_expira ? String(tenant.trial_expira).split("T")[0] : "" });
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [novoUser, setNovoUser] = useState(null);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios").then(setUsuarios).catch(() => {});
  }, [tenant.id]);

  const handleSave = async () => {
    setLoading(true); setErro("");
    try {
      await onSave(tenant.id, form);
      onClose();
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const handleAddUser = async () => {
    if (!novoUser?.nome || !novoUser?.login || !novoUser?.senha) return setErro("Preencha todos os campos do usuário");
    setLoading(true); setErro("");
    try {
      await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios", { method: "POST", body: novoUser });
      const usu = await apiFetch("/api/admin/tenants/" + tenant.id + "/usuarios");
      setUsuarios(usu);
      setNovoUser(null);
    } catch (err) { setErro(err.message); }
    setLoading(false);
  };

  const fieldStyle = { marginBottom: 14 };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#8888aa", marginBottom: 6, textTransform: "uppercase" };

  return (
    <Modal title={"Editar: " + tenant.nome} onClose={onClose} width={600}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Nome</label>
          <input value={form.nome} onChange={e => upd("nome", e.target.value)} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input type="email" value={form.email} onChange={e => upd("email", e.target.value)} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Plano</label>
          <select value={form.plano} onChange={e => upd("plano", e.target.value)}>
            <option value="basico">Básico</option>
            <option value="profissional">Profissional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Status</label>
          <select value={form.status} onChange={e => upd("status", e.target.value)}>
            <option value="ativo">Ativo</option>
            <option value="trial">Trial</option>
            <option value="suspenso">Suspenso</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Trial Expira</label>
          <input type="date" value={form.trial_expira} onChange={e => upd("trial_expira", e.target.value)} />
        </div>
      </div>

      {/* Usuários */}
      <div style={{ margin: "16px 0 10px", padding: "14px 0 0", borderTop: "1px solid #2a2a4a" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Usuários ({usuarios.length})</span>
          {!novoUser && <button onClick={() => setNovoUser({ nome: "", login: "", senha: "", email: "", role: "user" })} style={{ padding: "6px 14px", background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", borderRadius: 8, color: "#e74c3c", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Novo Usuário</button>}
        </div>
        {usuarios.map(u => (
          <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#16213e", borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
            <div>
              <span style={{ fontWeight: 600, color: "#fff" }}>{u.nome}</span>
              <span style={{ color: "#8888aa", marginLeft: 8 }}>({u.login})</span>
              <span style={{ marginLeft: 8, padding: "2px 8px", background: u.role === "admin" ? "rgba(231,76,60,.15)" : "rgba(52,152,219,.15)", color: u.role === "admin" ? "#e74c3c" : "#3498db", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{u.role}</span>
            </div>
            <span style={{ color: u.status === "ativo" ? "#2ecc71" : "#e74c3c", fontSize: 12, fontWeight: 600 }}>{u.status}</span>
          </div>
        ))}
        {novoUser && (
          <div style={{ background: "#16213e", border: "1px solid #2a2a4a", borderRadius: 10, padding: 14, marginTop: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e74c3c", marginBottom: 10 }}>Novo Usuário</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input placeholder="Nome" value={novoUser.nome} onChange={e => setNovoUser(f => ({ ...f, nome: e.target.value }))} style={{ fontSize: 13 }} />
              <input placeholder="Login" value={novoUser.login} onChange={e => setNovoUser(f => ({ ...f, login: e.target.value }))} style={{ fontSize: 13 }} />
              <input placeholder="Senha" value={novoUser.senha} onChange={e => setNovoUser(f => ({ ...f, senha: e.target.value }))} style={{ fontSize: 13 }} />
              <select value={novoUser.role} onChange={e => setNovoUser(f => ({ ...f, role: e.target.value }))} style={{ fontSize: 13 }}>
                <option value="user">Usuário</option>
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setNovoUser(null)} style={{ padding: "6px 14px", background: "none", border: "1px solid #2a2a4a", borderRadius: 6, color: "#8888aa", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
              <button onClick={handleAddUser} disabled={loading} style={{ padding: "6px 14px", background: "#e74c3c", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Adicionar</button>
            </div>
          </div>
        )}
      </div>

      {erro && <div style={{ background: "rgba(231,76,60,.15)", border: "1px solid rgba(231,76,60,.3)", color: "#e74c3c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 10, textAlign: "center" }}>{erro}</div>}

      <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "10px 24px", background: "#16213e", border: "1px solid #2a2a4a", borderRadius: 8, color: "#8888aa", fontSize: 14, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #e74c3c, #c0392b)", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: loading ? .7 : 1 }}>
          {loading ? "Salvando..." : "Salvar Alterações"}
        </button>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════
   TENANTS PAGE
   ══════════════════════════════════════════════ */

function TenantsPage({ tenants, loading, onReload, addToast }) {
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [novoModal, setNovoModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  const filtered = tenants.filter(t => {
    if (filtroStatus !== "todos" && t.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      if (!t.nome.toLowerCase().includes(q) && !t.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleCreate = async (form) => {
    await apiFetch("/api/admin/tenants", { method: "POST", body: form });
    addToast("Tenant criado!", form.nome, "🏢", "#2ecc71");
    onReload();
  };

  const handleEdit = async (id, form) => {
    await apiFetch("/api/admin/tenants/" + id, { method: "PUT", body: form });
    addToast("Tenant atualizado!", form.nome, "✏️", "#3498db");
    onReload();
  };

  const handleStatus = async (id, nome, status) => {
    await apiFetch("/api/admin/tenants/" + id + "/status", { method: "PATCH", body: { status } });
    addToast("Status alterado!", nome + " → " + statusLabels[status], "🔄", statusColors[status]);
    setMenuOpen(null);
    onReload();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Tenants</h1>
          <p style={{ color: "#8888aa", fontSize: 14 }}>{tenants.length} empresa(s) cadastrada(s)</p>
        </div>
        <button onClick={() => setNovoModal(true)} style={{ padding: "12px 24px", background: "linear-gradient(135deg, #e74c3c, #c0392b)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          + Novo Tenant
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome ou email..." style={{ flex: 1, maxWidth: 350 }} />
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ width: 160 }}>
          <option value="todos">Todos os Status</option>
          <option value="ativo">Ativo</option>
          <option value="trial">Trial</option>
          <option value="suspenso">Suspenso</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#8888aa" }}>Carregando...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#8888aa" }}>Nenhum tenant encontrado</div>
      ) : (
        <div style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 14, overflow: "visible" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", overflow: "visible" }}>
            <thead>
              <tr style={{ background: "#16213e" }}>
                {["Empresa", "Plano", "Status", "Usuários", "Locações", "Faturamento", "Trial Expira", "Ações"].map(h => (
                  <th key={h} style={{ padding: "14px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#8888aa", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #2a2a4a" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} style={{ borderBottom: "1px solid #2a2a4a", animation: `fadeIn .3s ease ${i * .03}s both` }}>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>{t.nome}</div>
                    <div style={{ fontSize: 12, color: "#8888aa", marginTop: 2 }}>{t.email}</div>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{ padding: "4px 10px", background: "#16213e", borderRadius: 6, fontSize: 12, fontWeight: 600, color: "#e0e0e0" }}>{planoLabels[t.plano] || t.plano}</span>
                  </td>
                  <td style={{ padding: "14px 16px" }}><StatusBadge status={t.status} /></td>
                  <td style={{ padding: "14px 16px", fontWeight: 600, color: "#fff" }}>{t.total_usuarios || 0}</td>
                  <td style={{ padding: "14px 16px", fontWeight: 600, color: "#fff" }}>{t.total_locacoes || 0}</td>
                  <td style={{ padding: "14px 16px", fontWeight: 600, color: "#2ecc71" }}>{fmt(t.faturamento || 0)}</td>
                  <td style={{ padding: "14px 16px", color: "#8888aa", fontSize: 13 }}>{fmtDate(t.trial_expira)}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <button id={"menu-btn-"+t.id} onClick={() => setMenuOpen(menuOpen === t.id ? null : t.id)} style={{ background: "#16213e", border: "1px solid #2a2a4a", borderRadius: 8, padding: "6px 12px", color: "#e0e0e0", cursor: "pointer", fontSize: 16 }}>⋮</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Menu flutuante fora da tabela */}
      {menuOpen && (() => {
        const t = tenants.find(x => x.id === menuOpen);
        if (!t) return null;
        const btn = document.getElementById("menu-btn-"+t.id);
        const rect = btn ? btn.getBoundingClientRect() : { top: 100, right: 100 };
        return (
          <>
            <div onClick={() => setMenuOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
            <div style={{ position: "fixed", top: rect.top + 40, right: window.innerWidth - rect.right, background: "#16213e", border: "1px solid #2a2a4a", borderRadius: 10, padding: 6, zIndex: 9999, minWidth: 180, boxShadow: "0 8px 30px rgba(0,0,0,.5)", animation: "fadeIn .2s ease" }}>
              <button onClick={() => { setEditModal(t); setMenuOpen(null); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#e0e0e0", fontSize: 13, cursor: "pointer", borderRadius: 6 }}>✏️ Editar</button>
              {t.status !== "ativo" && <button onClick={() => handleStatus(t.id, t.nome, "ativo")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#2ecc71", fontSize: 13, cursor: "pointer", borderRadius: 6 }}>✅ Ativar</button>}
              {t.status !== "suspenso" && <button onClick={() => handleStatus(t.id, t.nome, "suspenso")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#f39c12", fontSize: 13, cursor: "pointer", borderRadius: 6 }}>⏸️ Suspender</button>}
              {t.status !== "cancelado" && <button onClick={() => handleStatus(t.id, t.nome, "cancelado")} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", color: "#e74c3c", fontSize: 13, cursor: "pointer", borderRadius: 6 }}>❌ Cancelar</button>}
            </div>
          </>
        );
      })()}

      {novoModal && <NovoTenantModal onClose={() => setNovoModal(false)} onSave={handleCreate} />}
      {editModal && <EditarTenantModal tenant={editModal} onClose={() => setEditModal(null)} onSave={handleEdit} />}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN APP
   ══════════════════════════════════════════════ */

export default function App() {
  const [admin, setAdmin] = useState(getSession);
  const [page, setPage] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((title, sub, icon, color) => {
    const id = Date.now();
    setToasts(t => [...t, { id, title, sub, icon, color }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        apiFetch("/api/admin/stats"),
        apiFetch("/api/admin/tenants"),
      ]);
      setStats(s);
      setTenants(t);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (admin) loadData();
  }, [admin, loadData]);

  const handleLogout = () => { clearSession(); setAdmin(null); };

  if (!admin) return <><GlobalStyles /><LoginPage onLogin={setAdmin} /></>;

  return (
    <>
      <GlobalStyles />
      <ToastContainer toasts={toasts} />
      <Sidebar page={page} setPage={setPage} admin={admin} onLogout={handleLogout} />
      <div style={{ marginLeft: 240, padding: "30px 36px", minHeight: "100vh" }}>
        {page === "dashboard" && <Dashboard stats={stats} loading={loading} />}
        {page === "tenants" && <TenantsPage tenants={tenants} loading={loading} onReload={loadData} addToast={addToast} />}
      </div>
    </>
  );
}
